import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, WifiOff } from 'lucide-react'
import { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'

import { api } from '../../api/client'
import type { ProjectDetail, TenantUsage } from '../../api/types.gen'
import { formatStudioDate } from '../../lib/deliveryDate'
import { formatBytes } from '../../lib/format'
import {
  Badge,
  Banner,
  Card,
  ErrorState,
  ICON_STROKE,
  Progress,
  Skeleton,
  buttonClasses,
} from '../../ui'
import { DeliveryActivity } from './DeliveryActivity'
import { ProjectActions } from './ProjectActions'
import { ReceivedFiles } from './ReceivedFiles'
import { ShareLinksSection } from '../sharing/ShareLinksSection'
import { ResumablePanel } from '../uploads/ResumablePanel'
import { UploadDropzone } from '../uploads/UploadDropzone'
import { UploadQueue } from '../uploads/UploadQueue'
import { useUploadQueue } from '../../upload/useUploadQueue'

/**
 * `expires_in_days` lo cuenta Postgres: en el navegador dependería de su reloj.
 * Ahora lo devuelven por igual `show`, `store` y `update` — antes sólo `show`,
 * y aquí se daba por hecho que siempre venía.
 */
type Project = ProjectDetail


export function ProjectPage() {
  const { projectId = '' } = useParams()
  const queryClient = useQueryClient()
  const { items, batchError, resumable, summary, enqueue, cancel, resume } = useUploadQueue(projectId)

  const { data, isPending, error } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<{ data: Project }>(`/projects/${projectId}`),
    enabled: projectId !== '',
  })

  // El uso se lee del servidor, no del snapshot que devuelve el ticket: aquel
  // se calcula ANTES de subir, así que mostraría 0 B justo después de subir.
  const { data: usage } = useQuery({
    queryKey: ['tenant-usage'],
    queryFn: () => api.get<{ data: TenantUsage | null }>('/tenant/usage'),
  })

  const finished = summary.done + summary.failed

  useEffect(() => {
    if (summary.active === 0 && finished > 0) {
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tenant-usage'] })
    }
  }, [summary.active, finished, projectId, queryClient])

  if (isPending) {
    return (
      <section className="flex flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 w-full rounded-card" />
      </section>
    )
  }

  if (error) {
    return (
      <ErrorState
        icon={WifiOff}
        title="No se pudo cargar el proyecto"
        description="Casi siempre es la conexión. Vuelve a intentarlo."
        action={
          <Link className={buttonClasses('secondary')} to="/projects">
            Volver a proyectos
          </Link>
        }
      />
    )
  }

  const project = data.data
  const quota = usage?.data ?? null
  // `used_bytes` ya trae la suma hecha por el servidor —almacenado más lo que
  // está subiendo—, que es contra lo que él decide si cabe un archivo más.
  // Rehacer la cuenta aquí era la manera de que esta barra y la de «Plan y uso»
  // acabaran diciendo cosas distintas del mismo estudio.
  const quotaFull = quota !== null && quota.used_bytes >= quota.limit_bytes

  return (
    <section className="flex flex-col gap-4">
      <header>
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-label text-ink-subtle transition-colors hover:text-ink"
        >
          <ChevronLeft size={15} strokeWidth={ICON_STROKE} aria-hidden />
          Proyectos
        </Link>

        <div className="mt-1.5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-title font-semibold text-ink">{project.name}</h1>
              {/* La insignia sólo cuando dice algo. En un proyecto activo es
                  ruido: es el estado de casi todos. */}
              {project.status === 'archived' && <Badge tone="neutral">Archivado</Badge>}
              {project.status === 'draft' && <Badge tone="warning">Borrador</Badge>}
              {/* La caducidad se enseña SIEMPRE que exista: es la única cosa
                  que hace que el enlace de un cliente deje de abrirse sin que
                  nadie toque nada, y esconderla convierte eso en una sorpresa. */}
              {project.expires_at !== null && (
                <ExpiryBadge iso={project.expires_at} daysLeft={project.expires_in_days} />
              )}
            </div>

            <p className="text-body text-ink-muted" data-numeric>
              {project.asset_count} archivos · {formatBytes(project.storage_bytes)}
            </p>
          </div>

          <ProjectActions
            projectId={project.id}
            name={project.name}
            status={project.status}
            storageBytes={project.storage_bytes}
            expiresAt={project.expires_at}
          />
        </div>
      </header>

      <UploadDropzone onFiles={enqueue} disabled={quotaFull} />

      {/*
        Antes de la cola de subida y no al final de la página: lo que un cliente
        te ha mandado es una tarea pendiente TUYA, y enterrarla bajo lo que
        estás subiendo ahora mismo es la forma de que se quede sin mirar.
      */}
      <ReceivedFiles projectId={project.id} />

      {/*
        Debajo de lo recibido: aquello es una tarea pendiente y esto es
        información. Se pinta sólo si hay actividad.
      */}
      <DeliveryActivity projectId={project.id} />

      <ResumablePanel resumable={resumable} onResume={(target, file) => void resume(target, file)} />

      {quotaFull && (
        <Banner tone="danger" title="Se acabó el almacenamiento de tu plan" live="alert">
          <p>
            Tus galerías siguen publicadas y tus clientes pueden seguir
            descargando con normalidad. Sólo se bloquean las subidas nuevas.
          </p>
          <Link className={buttonClasses('primary', 'sm', 'mt-3')} to="/usage">
            Ver planes
          </Link>
        </Banner>
      )}

      {batchError !== null && (
        <Banner tone="danger" live="alert">
          {batchError}
        </Banner>
      )}

      {/*
        El progreso agregado.

        Es la operación crítica del producto —una boda de 400 RAW tarda más de
        dos horas en una conexión peruana— y hasta ahora era una línea de texto
        sin barra. Lo que decide si alguien abandona no es la velocidad sino
        saber que sigue avanzando.
      */}
      {summary.active > 0 && (
        <Card className="flex flex-col gap-2 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-body font-medium text-ink" data-numeric>
              Subiendo {summary.active} de {items.length}
            </span>
            <span className="text-subhead font-semibold text-ink" data-numeric>
              {summary.percent}%
            </span>
          </div>

          <Progress value={summary.percent} label="Progreso total de la subida" />

          <p className="text-caption text-ink-subtle" data-numeric>
            {formatBytes(summary.sentBytes)} de {formatBytes(summary.totalBytes)} · No cierres esta
            pestaña mientras suben.
          </p>
        </Card>
      )}

      <UploadQueue items={items} onCancel={cancel} />

      {quota !== null && (
        <p className="text-caption text-ink-subtle" data-numeric>
          Uso: {formatBytes(quota.stored_bytes)} de {formatBytes(quota.limit_bytes)}
          {/* Las sesiones abiertas reservan cuota hasta que expiran o se
              reconcilian (F1c); mostrarlas evita que el total parezca erróneo. */}
          {quota.inflight_bytes > 0 && <> · {formatBytes(quota.inflight_bytes)} reservados</>}
        </p>
      )}

      {/* Debajo de las subidas y no en otra pantalla: el trabajo del estudio es
          lineal —crear el proyecto, subir, compartir— y separar el último paso
          obliga a buscarlo justo cuando se acaba de terminar el anterior. */}
      <hr className="border-line" />

      <ShareLinksSection projectId={project.id} />
    </section>
  )
}

/**
 * «Caduca el 3 de setiembre» — o «Caducada» si ya pasó.
 *
 * En rojo el último tramo: a menos de una semana la fecha deja de ser un dato y
 * pasa a ser algo que hay que atender.
 */
function ExpiryBadge({ iso, daysLeft }: { iso: string; daysLeft: number | null }) {
  const dias = daysLeft ?? 0

  if (dias <= 0) return <Badge tone="danger">Caducada</Badge>

  // En el huso del estudio: sin eso, el mismo proyecto pone «8 de agosto» para
  // el fotógrafo en Lima y «9 de agosto» para quien lo mire desde Europa.
  return (
    <Badge tone={dias <= 7 ? 'warning' : 'neutral'}>Caduca el {formatStudioDate(iso)}</Badge>
  )
}
